import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from '@elkaso-app/web-design';
import { useGetCreditFacilityApi } from 'apis/bnpl';
import { useUpdateCreditLimitApi } from 'apis/bnpl';
import { formatAmount } from 'pages/bnpl/utils/bnpl-statuses';
import React from 'react';
import { useForm } from 'react-hook-form';

interface UpdateCreditLimitModalProps {
  trigger: JSX.Element;
  facilityId: number;
  currentLimit: number;
}

interface FormInputs {
  creditLimit: string;
}

export const UpdateCreditLimitModal = ({ trigger, facilityId, currentLimit }: UpdateCreditLimitModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { mutate: updateLimit, isLoading } = useUpdateCreditLimitApi();
  const { refetch } = useGetCreditFacilityApi(facilityId, { enabled: false });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: {
      creditLimit: '',
    },
  });

  const onCloseModal = () => {
    onClose();
    reset({ creditLimit: '' });
  };

  const onSubmit = (data: FormInputs) => {
    updateLimit(
      { facilityId, body: { creditLimit: parseFloat(data.creditLimit) } },
      {
        onSuccess: () => {
          refetch();
          onCloseModal();
        },
      }
    );
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={onCloseModal} closeOnOverlayClick={false} size='md'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Credit Limit</ModalHeader>
          <ModalBody>
            <Stack as='form' id='updateCreditLimitForm' spacing='lg' onSubmit={handleSubmit(onSubmit)} noValidate>
              <Text fontSize='sm' color='gray.500'>
                Current limit: {formatAmount(currentLimit)}
              </Text>

              <FormControl isInvalid={!!errors.creditLimit} isRequired>
                <FormLabel>New Credit Limit</FormLabel>
                <Input
                  type='number'
                  step='0.01'
                  min={0.01}
                  placeholder='Enter new credit limit'
                  {...register('creditLimit', {
                    required: 'Credit limit is required',
                    min: { value: 0.01, message: 'Credit limit must be greater than 0' },
                    validate: (value) => !isNaN(parseFloat(value)) || 'Please enter a valid number',
                  })}
                />
                <FormErrorMessage>{errors.creditLimit?.message}</FormErrorMessage>
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing='md'>
              <Button variant='outline' colorScheme='red' onClick={onCloseModal}>
                Cancel
              </Button>
              <Button
                variant='solid'
                colorScheme='red'
                type='submit'
                form='updateCreditLimitForm'
                isLoading={isLoading}>
                Update Limit
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
